import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Responsive, verticalCompactor, type ResponsiveLayouts } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  EmptyState,
  IconButton,
  MetricRibbon,
  PageHeader,
  PageLayout,
  buttonStyles,
  type MetricTone,
} from '@lognai/synapse-design-system-v2';
import {
  ArrowDown,
  ArrowUp,
  Boxes,
  CircleAlert,
  DollarSign,
  EllipsisVertical,
  ExternalLink,
  GripVertical,
  Package,
  Pencil,
  Pin,
  Plus,
  PinOff,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldAlert,
  Truck,
  X,
} from '@lognai/synapse-design-system-v2/icons';
import {
  PurchaseOrderStatusBadge,
  SalesOrderStatusBadge,
  buildPurchaseOrderDetailUrl,
  buildSalesOrderDetailUrl,
  formatCurrency,
  formatDate,
  useBackorders,
  usePurchaseOrders,
  useSalesOrderKpis,
  useSalesOrders,
} from '@lognai/plugin-orders-ui';
import { useInventoryKpis } from '@lognai/plugin-inventory-ui';
import { useShipmentKpis } from '@lognai/plugin-shipments-ui';
import { useOverview as useInvoiceOverview } from '@lognai/plugin-invoices-ui';
import { useAllTasks, useWarehouseKpis } from '@lognai/plugin-warehouse-ui';
import { executeAnalysis, executeVisualBatch, getVisual, formatMetric, type VisualDefinition } from '@lognai/plugin-analytics-ui';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { hasPermission } from '@lognai/plugin-sdk';
import { AskLognAIIcon, LatestInsightCard } from '@lognai/plugin-ai-core-ui';
import { RadarSection, type RadarContentShape } from '../components/dashboard/RadarSection';
import { resolveAvailableRadarModes } from '../components/dashboard/radar-modes';
import { useEntitledPlugins } from '../hooks/useEntitledPlugins';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CollaborationCard } from '../components/workspace/CollaborationCard';
import { ShipmentCalendarCard } from '../components/workspace/ShipmentCalendarCard';
import { ExpectedArrivalsCard } from '../components/workspace/ExpectedArrivalsCard';
import { ChartCardPicker } from '../components/workspace/ChartCardPicker';
import { WorkspaceKpiPicker } from '../components/workspace/WorkspaceKpiPicker';
import { WORKSPACE_KPI_REGISTRY, type WorkspaceKpiDefinition } from '../components/workspace/workspace-kpis';
import { customKpiId, customKpiVisualId, workspaceAiSource, workspaceAiStarters, type WorkspaceAiKind } from '../components/workspace/workspace-ai';
import { WorkspaceAiButton } from '../components/workspace/WorkspaceAiCreator';
import { optimizeWorkspaceLayout, type WorkspaceCardContext, type WorkspaceCardContent, type ReportWorkspaceCardContent } from '../services/api/workspace';
import { WorkspaceCardPicker } from '../components/workspace/WorkspaceCardPicker';
import { WorkspaceChartCard } from '../components/workspace/WorkspaceChartCard';
import { CardModuleLink } from '../components/workspace/CardModuleLink';
import { CardHeaderSlotContext } from '../components/workspace/card-header-slot';
import { useAuth } from '../hooks/useAuth';
import {
  summariseFailures,
  widgetFailureHint,
  widgetFailureReason,
} from '../utils/request-failure';
import {
  useSaveWorkspacePreferences,
  useWorkspacePreferences,
} from '../hooks/useWorkspacePreferences';
import {
  WORKSPACE_CARD_REGISTRY,
  WORKSPACE_GRID_COLUMNS,
  WORKSPACE_INSIGHTS_CARD,
  addWorkspaceCard,
  applyWorkspaceGridLayouts,
  chartCardDefinition,
  chartCardIdFor,
  createDefaultWorkspaceCardLayouts,
  isChartCardId,
  isVisualCardId,
  moveWorkspaceCard,
  reconcileWorkspaceLayout,
  removeWorkspaceCard,
  serializeWorkspaceLayout,
  toggleWorkspaceCardPin,
  visualIdFromCardId,
  workspaceLayoutsEqual,
  type WorkspaceCardDefinition,
  type WorkspaceBreakpoint,
  type WorkspaceCardId,
  type WorkspaceLayoutState,
} from '../components/workspace/workspace-layout';
import {
  WORKSPACE_PRESETS, WORKSPACE_DEFAULT_CHARTS, arrangeWorkspaceDefaultCards, inferWorkspacePreset, workspacePresetSelection, type WorkspacePresetId,
} from '../components/workspace/workspace-defaults';
import { prioritizeWorkspaceOrders } from '../components/workspace/workspace-attention';
import '../components/workspace/workspace-grid.css';

const DEFAULT_EMPTY_LAYOUT: WorkspaceLayoutState = {
  orderedCardIds: [],
  orderedKpiIds: [],
  pinnedCardIds: [],
  cardLayouts: createDefaultWorkspaceCardLayouts([]),
};

const WORKSPACE_BREAKPOINTS = { lg: 1024, md: 768, sm: 0 };
const WORKSPACE_COLUMNS = WORKSPACE_GRID_COLUMNS;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const WAREHOUSE_TASK_OVERDUE_DAYS = 7;
const CLOSED_PURCHASE_ORDER_STATUSES = new Set(['received', 'cancelled']);
const CLOSED_BACKORDER_STATUSES = new Set(['fulfilled', 'cancelled']);

function ageInDays(value: Date | string | undefined): number {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 0;
  return Math.max(0, Math.floor((Date.now() - timestamp) / DAY_IN_MS));
}

function useMobileBreakpoint() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return mobile;
}

function ErrorState({ message, error }: { message: string; error?: unknown }) {
  return (
    <div className="flex min-h-40 flex-col items-center justify-center gap-2 px-6 text-center">
      <CircleAlert className="h-5 w-5 text-warning-foreground" />
      <p className="text-sm font-medium text-foreground">Data unavailable</p>
      <p className="max-w-sm text-xs leading-5 text-muted-foreground">{message}</p>
      {/* What actually happened. Without it every panel says the same thing
          whether the reader lacks access, the server fell over, or they are
          simply being rate limited for a few seconds. */}
      {error !== undefined && error !== null && (
        <p className="max-w-sm text-xs leading-5 text-muted-foreground">{widgetFailureReason(error)}</p>
      )}
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="space-y-3 p-5" aria-label="Loading">
      <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
      <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
      <div className="h-4 w-full animate-pulse rounded bg-muted" />
      <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
    </div>
  );
}

interface WorkspaceCardShellProps {
  definition: WorkspaceCardDefinition;
  editing: boolean;
  hideHeader?: boolean;
  pinned: boolean;
  position: number;
  total: number;
  onMove: (id: WorkspaceCardId, direction: -1 | 1) => void;
  onTogglePin: (id: WorkspaceCardId) => void;
  onRemove: (id: WorkspaceCardId) => void;
  children: React.ReactNode;
}

function WorkspaceCardShell({
  definition,
  editing,
  hideHeader = false,
  pinned,
  position,
  total,
  onMove,
  onTogglePin,
  onRemove,
  children,
}: WorkspaceCardShellProps) {
  // Held in state rather than a ref so the body re-renders once the slot exists
  // and its portal actually lands.
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  return (
    <Card
      id={`workspace-card-${definition.id}`}
      className="workspace-card-shell relative flex h-full min-h-0 min-w-0 scroll-mt-24 flex-col overflow-hidden"
    >
      {!hideHeader && (
        <CardHeader
          className={`min-h-[66px] shrink-0 px-4 py-3 ${editing ? 'workspace-drag-handle' : ''}`}
        >
          <div className="flex min-w-0 items-center gap-3">
            {editing && (
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground"
              >
                <GripVertical className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <CardModuleLink
                  module={definition.module}
                  href={definition.moduleHref}
                  editing={editing}
                />
                {pinned && <Pin className="h-3 w-3 shrink-0 text-primary" aria-label="Pinned" />}
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <CardTitle size="sm" className="truncate">{definition.title}</CardTitle>
                {/* Filled by the card body via CardHeaderSlotContext. */}
                <div ref={setHeaderSlot} className="ml-auto flex shrink-0 items-center gap-1" />
              </div>
            </div>
          </div>

          {editing && (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 px-0"
                  aria-label={`Arrange ${definition.title}`}
                >
                  <EllipsisVertical className="h-4 w-4" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  disabled={position === 0}
                  onSelect={() => onMove(definition.id, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                  Move earlier
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  disabled={position === total - 1}
                  onSelect={() => onMove(definition.id, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                  Move later
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onSelect={() => onTogglePin(definition.id)}>
                  {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                  {pinned ? 'Unpin' : 'Pin to front'}
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item
                  className="text-destructive"
                  onSelect={() => onRemove(definition.id)}
                >
                  <X className="h-4 w-4" />
                  Remove card
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          )}
        </CardHeader>
      )}
      {hideHeader && editing && (
        <div className="absolute right-2 top-2 z-30 flex items-center rounded-md border border-border bg-surface">
          <span
            className="workspace-drag-handle flex h-8 w-8 cursor-grab items-center justify-center text-muted-foreground"
            aria-label={`Drag ${definition.title}`}
          >
            <GripVertical className="h-4 w-4" />
          </span>
          <DropdownMenu>
            <DropdownMenu.Trigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 px-0"
                aria-label={`Arrange ${definition.title}`}
              >
                <EllipsisVertical className="h-4 w-4" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Item
                disabled={position === 0}
                onSelect={() => onMove(definition.id, -1)}
              >
                <ArrowUp className="h-4 w-4" />
                Move earlier
              </DropdownMenu.Item>
              <DropdownMenu.Item
                disabled={position === total - 1}
                onSelect={() => onMove(definition.id, 1)}
              >
                <ArrowDown className="h-4 w-4" />
                Move later
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item onSelect={() => onTogglePin(definition.id)}>
                {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                {pinned ? 'Unpin' : 'Pin to front'}
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                className="text-destructive"
                onSelect={() => onRemove(definition.id)}
              >
                <X className="h-4 w-4" />
                Remove card
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <CardHeaderSlotContext.Provider value={headerSlot}>
          {children}
        </CardHeaderSlotContext.Provider>
      </div>
    </Card>
  );
}

function WorkspaceInsertSlot({ label, className, canAddCharts, onChoose }: {
  label: string; className?: string; canAddCharts: boolean; onChoose: (kind: WorkspaceAiKind) => void;
}) {
  const [point, setPoint] = useState<{ x: number; y: number; height: number }>();
  return <DropdownMenu>
    <DropdownMenu.Trigger asChild><button type="button" className={`workspace-insert-card ${className ?? ''}`} aria-label={label} title={label} onPointerDown={(event) => {
      if (event.button !== 0) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      setPoint({ x: event.clientX - bounds.left, y: event.clientY - bounds.top, height: bounds.height });
    }} onKeyDown={() => setPoint(undefined)} /></DropdownMenu.Trigger>
    <DropdownMenu.Content side="bottom" align="start" alignOffset={point?.x ?? 0} sideOffset={point ? point.y - point.height + 4 : 4}>
      <DropdownMenu.Item onSelect={() => onChoose('card')}>Add card here</DropdownMenu.Item>
      {canAddCharts && <DropdownMenu.Item onSelect={() => onChoose('chart')}>Add chart here</DropdownMenu.Item>}
      <DropdownMenu.Item onSelect={() => onChoose('kpi')}>Add KPI to strip</DropdownMenu.Item>
    </DropdownMenu.Content>
  </DropdownMenu>;
}

function WorkspaceRadarBody({ onContent }: { onContent: ReportWorkspaceCardContent }) {
  const reportContent = useCallback((content: RadarContentShape) => onContent('radar', content), [onContent]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(420);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => setHeight(Math.max(240, container.getBoundingClientRect().height - 45));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-full min-h-[240px]">
      <RadarSection height={height} embedded className="h-full" onContentChange={reportContent} />
    </div>
  );
}

function MiniMetric({
  label,
  value,
  tone = 'neutral',
  askPrompt,
}: {
  label: string;
  value: React.ReactNode;
  tone?: 'neutral' | 'warning' | 'success' | 'primary';
  askPrompt?: string;
}) {
  const toneClass = {
    neutral: 'text-foreground',
    warning: 'text-warning-foreground',
    success: 'text-success-foreground',
    primary: 'text-primary',
  }[tone];
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <p className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </p>
        {askPrompt && <AskLineItemButton label={label} prompt={askPrompt} />}
      </div>
      <p className={`mt-1 truncate text-xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

interface AskPageContext {
  pluginId: string;
  entityType: string;
  entityId: string;
  url: string;
}

function openAskLognAI(prompt: string, pageContext?: AskPageContext) {
  window.dispatchEvent(new CustomEvent('lognai:open-ai-chat', { detail: { prompt, pageContext } }));
}

function AskLineItemButton({
  label,
  prompt,
  pageContext,
  className = '',
}: {
  label: string;
  prompt: string;
  pageContext?: AskPageContext;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-primary transition-colors hover:bg-primary-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${className}`}
      aria-label={`Ask LognAI about ${label}`}
      title={`Ask LognAI about ${label}`}
      onClick={(event) => {
        event.stopPropagation();
        openAskLognAI(prompt, pageContext);
      }}
    >
      <AskLognAIIcon size={13} aria-hidden="true" />
    </button>
  );
}

export default function WorkspacePage() {
  const navigate = useNavigate();
  const { user, activeCompanyId } = useAuth();
  const mobile = useMobileBreakpoint();
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const [activeBreakpoint, setActiveBreakpoint] = useState<WorkspaceBreakpoint>('lg');
  const [insightsEmpty, setInsightsEmpty] = useState(false);
  const [emptyInsightsRows, setEmptyInsightsRows] = useState(2);
  const [insightsContent, setInsightsContent] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!insightsEmpty) return;
    const content = insightsContent?.querySelector('section');
    if (!content) return;
    const measure = () => {
      const height = content.getBoundingClientRect().height;
      if (height > 0) {
        // Grid rows are 40px with a 12px gap. Include wrapped empty-state
        // text at narrow card widths without keeping the populated height.
        setEmptyInsightsRows(Math.max(1, (Math.ceil(height) + 12) / 52));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [insightsEmpty, insightsContent]);

  const canViewGlobalInsights = hasPermission('global_insights:news:read');
  const canViewSalesOrders = hasPermission('order:sales:read');
  const canViewPurchaseOrders = hasPermission('order:purchase:read');
  const canViewBackorders = hasPermission('order:backorder:read');
  const canViewInventory = hasPermission('inventory:item:read');
  const canViewShipments = hasPermission('shipment:shipment:read');
  const canViewWarehouse = hasPermission('warehouse:warehouse:read');
  const canViewWarehouseTasks = hasPermission('warehouse:task:read');

  const { plugins: effectivePlugins, hasPlugin } = useEntitledPlugins();

  const radarModes = useMemo(
    () => resolveAvailableRadarModes(hasPermission, hasPlugin),
    [hasPlugin]
  );

  const visibleDefinitions = useMemo(
    () =>
      WORKSPACE_CARD_REGISTRY.filter((definition) => {
        // The radar is one frame over two modules, so a single `requiredPlugin`
        // cannot express it — each mode is gated on its own permission AND its
        // own plugin entitlement. This used to short-circuit on permission
        // alone, which showed the card (and an empty map) to companies that had
        // not bought either module.
        if (definition.id === 'radar') return radarModes.length > 0;
        if (definition.requiredPlugin && !effectivePlugins.has(definition.requiredPlugin))
          return false;
        return definition.permissions.every((permission) => hasPermission(permission));
      }),
    [
      radarModes,
      canViewGlobalInsights,
      canViewBackorders,
      canViewInventory,
      canViewPurchaseOrders,
      canViewSalesOrders,
      canViewShipments,
      canViewWarehouse,
      canViewWarehouseTasks,
      effectivePlugins,
    ]
  );
  const queryClient = useQueryClient();
  const preferenceQuery = useWorkspacePreferences(user?.id, activeCompanyId);

  // Declared before the card-definition memos below, which read the draft to
  // discover chart cards added in this editing session.
  const [savedLayout, setSavedLayout] = useState<WorkspaceLayoutState>(DEFAULT_EMPTY_LAYOUT);
  const [draftLayout, setDraftLayout] = useState<WorkspaceLayoutState>(DEFAULT_EMPTY_LAYOUT);
  const latestDraft = useRef(draftLayout);
  latestDraft.current = draftLayout;

  const canAddCharts = hasPermission('analytics:dashboard:read');
  const [chartPickerOpen, setChartPickerOpen] = useState(false);
  const [cardPickerOpen, setCardPickerOpen] = useState(false);
  const [cardPickerAi, setCardPickerAi] = useState(false);
  const openCardPicker = (withAi = false) => { setCardPickerAi(withAi); setCardPickerOpen(true); };
  const [kpiPickerOpen, setKpiPickerOpen] = useState(false);
  const [insertionIndex, setInsertionIndex] = useState<number | undefined>();
  const [cardContent, setCardContent] = useState<Record<string, WorkspaceCardContent>>({});
  const reportCardContent = useCallback((id: string, content: WorkspaceCardContent) => {
    setCardContent((current) => JSON.stringify(current[id]) === JSON.stringify(content) ? current : { ...current, [id]: content });
  }, []);
  const [createdVisuals, setCreatedVisuals] = useState<Record<string, VisualDefinition>>({});
  const canUseAiView = (view: string) => {
    const source = workspaceAiSource(view);
    return !!source && effectivePlugins.has(source.plugin) && hasPermission(source.permission);
  };
  const customKpiIds = canAddCharts ? [...new Set([
    ...(preferenceQuery.data?.orderedKpiIds ?? []), ...draftLayout.orderedKpiIds,
  ])].filter((id) => customKpiVisualId(id)) : [];
  const customKpiMetadata = useQueries({ queries: customKpiIds.map((id) => {
    const visualId = customKpiVisualId(id)!;
    return {
      queryKey: ['workspace', 'custom-kpi-definition', user?.id, activeCompanyId, visualId],
      queryFn: () => getVisual(visualId), initialData: createdVisuals[visualId],
      enabled: !!user?.id && !!activeCompanyId, staleTime: 60_000, retry: false,
    };
  }) });
  const availableKpiDefinitions: WorkspaceKpiDefinition[] = [...WORKSPACE_KPI_REGISTRY.filter((definition) =>
    effectivePlugins.has(definition.plugin) && hasPermission(definition.permission) &&
    (!definition.measure || canAddCharts)
  ), ...customKpiIds.flatMap((id, index): WorkspaceKpiDefinition[] => {
    const visual = customKpiMetadata[index]?.data;
    if (visual && !canUseAiView(visual.analysis.view)) return [];
    return [{ id, visualId: customKpiVisualId(id), label: visual?.title ?? 'Custom KPI', description: visual?.businessQuestion ?? 'AI-created metric',
      plugin: 'analytics', permission: 'analytics:dashboard:read', module: 'Analytics', href: '/analytics', icon: Package, tone: 'primary' }];
  })];
  const availableKpiIds = availableKpiDefinitions.map((definition) => definition.id);
  const availableKpiKey = availableKpiIds.join('|');
  /** Titles for pinned charts, learned once each chart executes. */
  const [chartTitles, setChartTitles] = useState<Record<string, string>>({});

  // Chart cards are user-chosen, so they are not in the static registry. Both
  // sources matter: the saved preference (or a saved chart is dropped as an
  // unknown id on the next load) AND the live draft (or a chart just added in
  // edit mode has no definition and renders as nothing until after a save).
  const staticVisibleIds = useMemo(
    () => visibleDefinitions.map((definition) => definition.id),
    [visibleDefinitions]
  );
  const defaultCharts = WORKSPACE_DEFAULT_CHARTS.filter((chart) =>
    canAddCharts && effectivePlugins.has(chart.plugin) &&
    chart.permissions.every((permission) => hasPermission(permission))
  );
  const defaultChartIds = defaultCharts.map((chart) => chart.id);
  const defaultChartKey = defaultChartIds.join('|');

  /**
   * The card ids a persisted layout is reconciled against.
   *
   * Charts are not in the static registry, so they have to come from the
   * preference being reconciled — and ONLY from it. Feeding in charts that exist
   * only in the unsaved draft makes this set change the moment a chart is added,
   * which re-runs the load effect below; that effect reconciles against the
   * saved preference, which does not have the chart yet, so it appends the chart
   * at the bottom and overwrites the draft mid-edit. It also wrote that result
   * into `savedLayout`, which is what Cancel restores — so the cancelled chart
   * came back.
   */
  const reconcileIdsFor = (saved: { orderedCardIds?: string[] } | null | undefined) => [
    WORKSPACE_INSIGHTS_CARD.id,
    ...staticVisibleIds,
    ...defaultChartIds,
    ...(canAddCharts ? (saved?.orderedCardIds ?? []).filter(isVisualCardId) : []),
  ];

  const defaultPresetId = inferWorkspacePreset(user?.roles ?? [], staticVisibleIds);
  const defaultSelection = workspacePresetSelection(defaultPresetId, staticVisibleIds, availableKpiIds);
  defaultSelection.cardIds = arrangeWorkspaceDefaultCards(defaultPresetId, defaultSelection.cardIds, defaultChartIds);

  const savedChartCardIds = useMemo(
    () => (preferenceQuery.data?.orderedCardIds ?? []).filter(isVisualCardId),
    [preferenceQuery.data]
  );
  const draftChartCardIds = draftLayout.orderedCardIds.filter(isVisualCardId);
  const draftChartKey = draftChartCardIds.join('|');

  const visibleIds = useMemo(
    () => [...staticVisibleIds, ...defaultChartIds, ...(canAddCharts ? savedChartCardIds : [])],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canAddCharts, savedChartCardIds, staticVisibleIds, defaultChartKey]
  );
  const visibleKey = visibleIds.join('|');

  // Rendering is a separate concern from reconciliation: a chart added in this
  // session has no definition yet but still has to draw, so the draft's charts
  // are included here even though they are kept out of `visibleIds`.
  const definitionById = useMemo(() => {
    const map = new Map(visibleDefinitions.map((definition) => [definition.id, definition]));
    map.set(WORKSPACE_INSIGHTS_CARD.id, WORKSPACE_INSIGHTS_CARD);
    for (const id of [...visibleIds, ...draftChartCardIds]) {
      if (isVisualCardId(id) && !map.has(id)) {
        map.set(id, chartCardDefinition(id, chartTitles[visualIdFromCardId(id)] ??
          WORKSPACE_DEFAULT_CHARTS.find((chart) => chart.id === id)?.title));
      }
    }
    return map;
    // draftChartKey stands in for the array identity, which changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartTitles, draftChartKey, visibleDefinitions, visibleIds]);
  const savePreferences = useSaveWorkspacePreferences(user?.id, activeCompanyId);
  const identityKey = `${user?.id ?? 'anonymous'}:${activeCompanyId ?? 'none'}`;

  const [loadedIdentity, setLoadedIdentity] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const previousIdentity = useRef(identityKey);
  const optimizeSequence = useRef(0);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizationNote, setOptimizationNote] = useState<string | null>(null);
  const [optimizationError, setOptimizationError] = useState<string | null>(null);
  useEffect(() => {
    optimizeSequence.current += 1;
    setOptimizing(false); setOptimizationNote(null); setOptimizationError(null);
    return () => { optimizeSequence.current += 1; };
  }, [identityKey, editing]);
  const hasDraftCards = draftLayout.orderedCardIds.length > 0;

  useLayoutEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;
    const measure = () => {
      const measured = container.getBoundingClientRect().width;
      setGridWidth(measured > 0 ? measured : window.innerWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [loadedIdentity, visibleKey, hasDraftCards]);

  useEffect(() => {
    if (previousIdentity.current === identityKey) return;
    previousIdentity.current = identityKey;
    setLoadedIdentity(null);
    setSavedLayout(DEFAULT_EMPTY_LAYOUT);
    setDraftLayout(DEFAULT_EMPTY_LAYOUT);
    setEditing(false);
    setCardPickerOpen(false);
    setChartPickerOpen(false);
    setKpiPickerOpen(false);
    setInsertionIndex(undefined);
    setCreatedVisuals({});
    setCardContent({});
    setLoadFailed(false);
    setSaveError(null);
    savePreferences.reset();
  }, [identityKey]);

  useEffect(() => {
    // Never overwrite an edit in progress. A background refetch or a permission
    // change would otherwise discard everything the user has arranged so far.
    if (editing) return;
    if (!user?.id || !activeCompanyId) {
      const fallback = reconcileWorkspaceLayout(null, reconcileIdsFor(null), availableKpiIds, defaultSelection.kpiIds, defaultSelection.cardIds);
      setSavedLayout(fallback);
      setDraftLayout(fallback);
      setLoadedIdentity(identityKey);
      return;
    }
    if (!preferenceQuery.isSuccess && !preferenceQuery.isError) return;

    const source = preferenceQuery.isSuccess ? preferenceQuery.data : null;
    const reconciled = reconcileWorkspaceLayout(source, reconcileIdsFor(source), availableKpiIds, defaultSelection.kpiIds, defaultSelection.cardIds);
    setSavedLayout(reconciled);
    setDraftLayout(reconciled);
    setLoadedIdentity(identityKey);
    setLoadFailed(preferenceQuery.isError);
    setSaveError(null);
    // `editing` is read but deliberately not a dependency: re-running on the
    // true -> false transition would reset the draft from a preference that may
    // not have refetched yet, undoing the save that just closed edit mode.
    // `reconcileIdsFor` is recreated every render and is covered by visibleKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeCompanyId,
    identityKey,
    preferenceQuery.data,
    preferenceQuery.isError,
    preferenceQuery.isSuccess,
    user?.id,
    visibleKey,
    availableKpiKey,
    defaultPresetId,
  ]);

  const unsaved = loadFailed || !workspaceLayoutsEqual(draftLayout, savedLayout);

  /**
   * Both guards below used the browser's `window.confirm` — unstyled, unthemed
   * and visibly not part of the product (QA ledger SHIP-006). The link guard
   * cannot simply await an answer, because a click listener has to decide
   * synchronously, so it now always cancels the navigation and replays it once
   * the user confirms.
   */
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  useEffect(() => {
    if (!editing || !unsaved) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const protectLinks = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest('a');
      if (!anchor || event.defaultPrevented) return;
      const href = anchor.getAttribute('href');
      // Nothing to replay for an in-page or scripted anchor: let it through
      // rather than blocking it behind a prompt we could never honour.
      if (!href || href.startsWith('#')) return;
      event.preventDefault();
      event.stopPropagation();
      setPendingHref(href);
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', protectLinks, true);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', protectLinks, true);
    };
  }, [editing, unsaved]);

  const {
    data: orderKpis,
    isLoading: ordersLoading,
    isError: ordersError,
    error: ordersFailure,
  } = useSalesOrderKpis({ enabled: canViewSalesOrders && effectivePlugins.has('orders') });
  const {
    data: inventoryKpis,
    isLoading: inventoryLoading,
    isError: inventoryError,
    error: inventoryFailure,
  } = useInventoryKpis({ enabled: canViewInventory && effectivePlugins.has('inventory') });
  const {
    data: shipmentKpis,
    isLoading: shipmentLoading,
    isError: shipmentError,
    error: shipmentFailure,
  } = useShipmentKpis({ enabled: canViewShipments && effectivePlugins.has('shipments') });
  const {
    data: invoiceOverview,
    isLoading: invoiceOverviewLoading,
    isError: invoiceOverviewError,
    error: invoiceOverviewFailure,
  } = useInvoiceOverview({
    enabled: effectivePlugins.has('invoices') && hasPermission('invoices:invoice:read'),
  });
  const {
    data: warehouseKpis,
    isLoading: warehouseLoading,
    isError: warehouseError,
    error: warehouseFailure,
  } = useWarehouseKpis({ enabled: canViewWarehouse && effectivePlugins.has('warehouse') });
  const {
    data: salesOrdersData,
    isLoading: salesOrdersLoading,
    isError: salesOrdersError,
    error: salesOrdersFailure,
  } = useSalesOrders(
    { limit: 100, page: 1 },
    { enabled: canViewSalesOrders && effectivePlugins.has('orders') }
  );
  const {
    data: purchaseOrdersData,
    isLoading: purchaseOrdersLoading,
    isError: purchaseOrdersError,
    error: purchaseOrdersFailure,
  } = usePurchaseOrders(
    { limit: 5, page: 1 },
    { enabled: canViewPurchaseOrders && effectivePlugins.has('orders') }
  );
  const purchaseOrderRiskEnabled =
    definitionById.has('purchase-orders-at-risk') &&
    canViewPurchaseOrders &&
    effectivePlugins.has('orders');
  const {
    data: purchaseOrderRiskData,
    isLoading: purchaseOrderRiskLoading,
    isError: purchaseOrderRiskError,
    error: purchaseOrderRiskFailure,
  } = usePurchaseOrders({ limit: 100, page: 1 }, { enabled: purchaseOrderRiskEnabled });
  const {
    data: backordersData,
    isLoading: backordersLoading,
    isError: backordersError,
    error: backordersFailure,
  } = useBackorders(
    { limit: 100, page: 1 },
    {
      enabled: purchaseOrderRiskEnabled && canViewBackorders,
    }
  );
  const warehouseTaskAgingEnabled =
    definitionById.has('warehouse-task-aging') &&
    canViewWarehouseTasks &&
    effectivePlugins.has('warehouse');
  const {
    data: warehouseTasksData,
    isLoading: warehouseTasksLoading,
    isError: warehouseTasksError,
    error: warehouseTasksFailure,
  } = useAllTasks({ limit: 100, page: 1 }, { enabled: warehouseTaskAgingEnabled });

  // Say it once, at the top, instead of a dozen identical "Data unavailable"
  // tiles. Every widget here is an independent query, so a single cause — the
  // gateway's rate limiter, most often — fails all of them at the same instant
  // and the page reads as a total outage with no hint that it is transient and
  // no way back but guessing when to reload (QA ledger AUTH-004, ORD-009).
  const loadFailure = useMemo(
    () =>
      summariseFailures([
        ordersFailure,
        inventoryFailure,
        shipmentFailure,
        invoiceOverviewFailure,
        warehouseFailure,
        salesOrdersFailure,
        purchaseOrdersFailure,
        purchaseOrderRiskFailure,
        backordersFailure,
        warehouseTasksFailure,
        preferenceQuery.error,
      ]),
    [
      ordersFailure, inventoryFailure, shipmentFailure, invoiceOverviewFailure, warehouseFailure,
      salesOrdersFailure, purchaseOrdersFailure, purchaseOrderRiskFailure, backordersFailure,
      warehouseTasksFailure, preferenceQuery.error,
    ]
  );
  const [retrying, setRetrying] = useState(false);
  const retryFailedPanels = useCallback(() => {
    setRetrying(true);
    // Refetch only what actually failed — retrying the whole page would spend
    // more of the very budget that a rate-limited reader is short of.
    void queryClient
      .refetchQueries({ type: 'active', predicate: (query) => query.state.status === 'error' })
      .finally(() => setRetrying(false));
  }, [queryClient]);

  const salesOrders = prioritizeWorkspaceOrders(salesOrdersData?.items ?? []);
  const purchaseOrders = (purchaseOrdersData?.items ?? []).slice(0, 3);
  const purchaseOrdersAtRisk = useMemo(() => {
    const openOrders = (purchaseOrderRiskData?.items ?? []).filter(
      (order) => !CLOSED_PURCHASE_ORDER_STATUSES.has(order.status)
    );
    const rankedByValue = [...openOrders].sort((left, right) => right.total - left.total);
    const highValueCount = rankedByValue.length
      ? Math.max(1, Math.ceil(rankedByValue.length * 0.2))
      : 0;
    const highValueIds = new Set(rankedByValue.slice(0, highValueCount).map((order) => order.id));
    const blockedDemandPurchaseOrderIds = new Set(
      (backordersData?.items ?? [])
        .filter(
          (backorder) =>
            backorder.linkedPurchaseOrderId && !CLOSED_BACKORDER_STATUSES.has(backorder.status)
        )
        .map((backorder) => backorder.linkedPurchaseOrderId as string)
    );

    return openOrders
      .map((order) => {
        const overdueDays = order.expectedDeliveryDate ? ageInDays(order.expectedDeliveryDate) : 0;
        const risks = [
          overdueDays > 0 && { key: 'overdue', label: `${overdueDays}d overdue` },
          !order.expectedDeliveryDate && { key: 'missing-eta', label: 'Missing ETA' },
          highValueIds.has(order.id) && { key: 'high-value', label: 'High value' },
          blockedDemandPurchaseOrderIds.has(order.id) && {
            key: 'blocked-demand',
            label: 'Blocked demand',
          },
        ].filter(Boolean) as Array<{ key: string; label: string }>;
        return { order, overdueDays, risks };
      })
      .filter((item) => item.risks.length > 0)
      .sort(
        (left, right) =>
          Number(right.risks.some((risk) => risk.key === 'blocked-demand')) -
            Number(left.risks.some((risk) => risk.key === 'blocked-demand')) ||
          right.overdueDays - left.overdueDays ||
          right.order.total - left.order.total
      )
      .slice(0, 3);
  }, [backordersData?.items, purchaseOrderRiskData?.items]);
  const warehouseTaskAgingRows = useMemo(() => {
    const labels = {
      receive: 'Receiving',
      pick: 'Picking',
      put_away: 'Putaway',
      transfer: 'Transfers',
    } as const;
    const openTasks = (warehouseTasksData?.items ?? []).filter(
      (task) => task.status !== 'completed' && task.status !== 'cancelled'
    );

    return (Object.keys(labels) as Array<keyof typeof labels>).map((type) => {
      const overdueTasks = openTasks
        .filter(
          (task) => task.type === type && ageInDays(task.createdAt) > WAREHOUSE_TASK_OVERDUE_DAYS
        )
        .sort((left, right) => ageInDays(right.createdAt) - ageInDays(left.createdAt));
      return {
        type,
        label: labels[type],
        count: overdueTasks.length,
        oldestDays: overdueTasks.length ? ageInDays(overdueTasks[0].createdAt) : 0,
      };
    });
  }, [warehouseTasksData?.items]);
  const pendingOrders = orderKpis?.ordersByStatus?.pending ?? 0;
  /**
   * Warehouse capacity, in the units a warehouse document stores.
   *
   * This card used to read `totalSquareMeters` / `usedSquareMeters` /
   * `usedPalletPositions` off the KPI response. None of those fields exists on
   * a warehouse — its capacity is `{ pallets, squareFeet, cubicFeet }` — so
   * the card showed "Utilization 0%", "Used space 0 m²", "Pallet positions 0"
   * and a 0% progress bar, in square METRES, on the primary landing page
   * (QA ledger WH-012).
   *
   * Utilisation is gone rather than recomputed: nothing anywhere records how
   * much of a warehouse is occupied. `Location.currentOccupancy` is written by
   * nothing (WH-003) and there is no warehouse-level equivalent. The honest
   * proxy is slot occupancy, which inventory's command centre already computes
   * per warehouse as `binsUsed`/`binsTotal`; surfacing it here is a real
   * feature rather than a relabel, so this card now shows capacity and says
   * nothing about usage.
   */
  const warehouseSquareFeet = warehouseKpis?.totalCapacity?.totalSquareFeet ?? 0;
  const warehousePallets = warehouseKpis?.totalCapacity?.totalPallets ?? 0;
  const warehouseCubicFeet = warehouseKpis?.totalCapacity?.totalCubicFeet ?? 0;

  const moveCard = (id: WorkspaceCardId, direction: -1 | 1) => {
    const index = draftLayout.orderedCardIds.indexOf(id);
    setDraftLayout(moveWorkspaceCard(draftLayout, id, index + direction));
    setSaveError(null);
  };

  const addChartCard = (visual: { id: string; title: string }) => {
    setChartTitles((current) => ({ ...current, [visual.id]: visual.title }));
    setDraftLayout((current) => addWorkspaceCard(current, chartCardIdFor(visual.id), insertionIndex));
    setInsertionIndex(undefined);
    setChartPickerOpen(false);
    setSaveError(null);
  };

  const addCard = (id: WorkspaceCardId) => {
    setDraftLayout((current) => addWorkspaceCard(current, id, insertionIndex));
    setInsertionIndex(undefined);
    setCardPickerOpen(false);
    setSaveError(null);
  };

  const addKpi = (id: string) => {
    setDraftLayout((current) => current.orderedKpiIds.includes(id) ? current : {
      ...current, orderedKpiIds: [...current.orderedKpiIds, id],
    });
    setKpiPickerOpen(false);
    setInsertionIndex(undefined);
    setSaveError(null);
  };

  const addAiWidget = (visual: VisualDefinition, kind: WorkspaceAiKind) => {
    setCreatedVisuals((current) => ({ ...current, [visual.id]: visual }));
    if (kind === 'kpi') addKpi(customKpiId(visual.id));
    else {
      setChartTitles((current) => ({ ...current, [visual.id]: visual.title }));
      setDraftLayout((current) => addWorkspaceCard(current, kind === 'card' ? `card:${visual.id}` : chartCardIdFor(visual.id), insertionIndex));
      setInsertionIndex(undefined);
    }
    setCardPickerOpen(false); setKpiPickerOpen(false); setChartPickerOpen(false); setSaveError(null);
  };

  const removeKpi = (id: string) => {
    setDraftLayout((current) => ({ ...current, orderedKpiIds: current.orderedKpiIds.filter((item) => item !== id) }));
    setSaveError(null);
  };

  const moveKpi = (id: string, direction: -1 | 1) => {
    setDraftLayout((current) => {
      const next = [...current.orderedKpiIds];
      const index = next.indexOf(id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...current, orderedKpiIds: next };
    });
    setSaveError(null);
  };

  const removeCard = (id: WorkspaceCardId) => {
    setDraftLayout(removeWorkspaceCard(draftLayout, id));
    setSaveError(null);
  };

  const saveLayout = async () => {
    setSaveError(null);
    try {
      const saved = await savePreferences.mutateAsync(serializeWorkspaceLayout(draftLayout));
      // Against the ids in the response, not `visibleIds` — the preference query
      // has not refetched yet, so `visibleIds` still lacks any chart added in
      // this session and reconciling against it would drop the chart just saved.
      const reconciled = reconcileWorkspaceLayout(saved, reconcileIdsFor(saved), availableKpiIds, defaultSelection.kpiIds, defaultSelection.cardIds);
      setSavedLayout(reconciled);
      setDraftLayout(reconciled);
      setLoadFailed(false);
      setEditing(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Workspace layout could not be saved');
    }
  };

  const applyPreset = (presetId: WorkspacePresetId) => {
    const selection = workspacePresetSelection(presetId, staticVisibleIds, availableKpiIds);
    const chartIds = [...new Set([...defaultChartIds, ...draftChartCardIds])];
    // Preserve selected charts while restoring card geometry and the KPI strip.
    setDraftLayout(reconcileWorkspaceLayout(
      null, [WORKSPACE_INSIGHTS_CARD.id, ...staticVisibleIds, ...chartIds], availableKpiIds,
      selection.kpiIds, arrangeWorkspaceDefaultCards(presetId, selection.cardIds, chartIds)
    ));
    setSaveError(null);
  };

  const resetDraft = () => {
    setConfirmingReset(false);
    applyPreset(defaultPresetId);
  };

  const cancelEditing = () => {
    setDraftLayout(savedLayout);
    setEditing(false);
    setKpiPickerOpen(false);
    setSaveError(null);
  };

  const layoutReady = loadedIdentity === identityKey;

  const localFocusItems = [
    {
      id: 'orders',
      label: 'Orders',
      value: ordersLoading
        ? null
        : ordersError
          ? 'Unavailable'
          : (orderKpis?.totalOrders.toLocaleString() ?? '0'),
      hint: ordersError ? widgetFailureHint(ordersFailure) : `${pendingOrders.toLocaleString()} pending`,
      icon: Package,
      tone: 'primary',
      target: 'recent-sales-orders' as WorkspaceCardId,
      warning: pendingOrders > 0,
    },
    {
      id: 'inventory',
      label: 'Inventory',
      value: inventoryLoading
        ? null
        : inventoryError
          ? 'Unavailable'
          : (inventoryKpis?.totalItems.toLocaleString() ?? '0'),
      hint: inventoryError
        ? widgetFailureHint(inventoryFailure)
        : `${(inventoryKpis?.itemsBelowReorderPoint ?? 0).toLocaleString()} low stock`,
      icon: Boxes,
      tone: 'warning',
      target: 'inventory-health' as WorkspaceCardId,
      warning: (inventoryKpis?.itemsBelowReorderPoint ?? 0) > 0,
    },
    {
      id: 'shipments',
      label: 'Active shipments',
      value: shipmentLoading
        ? null
        : shipmentError
          ? 'Unavailable'
          : (shipmentKpis?.inTransitCount.toLocaleString() ?? '0'),
      hint: shipmentError
        ? widgetFailureHint(shipmentFailure)
        : `${(shipmentKpis?.onHoldCount ?? 0).toLocaleString()} on hold`,
      icon: Truck,
      tone: 'info',
      target: 'shipment-flow' as WorkspaceCardId,
      warning: (shipmentKpis?.onHoldCount ?? 0) > 0,
    },
    {
      id: 'revenue',
      label: 'Revenue',
      value: ordersLoading
        ? null
        : ordersError
          ? 'Unavailable'
          : formatCurrency(orderKpis?.totalValue ?? 0),
      hint: ordersError
        ? widgetFailureHint(ordersFailure)
        : `Avg ${formatCurrency(orderKpis?.averageOrderValue ?? 0)} per order`,
      icon: DollarSign,
      tone: 'success',
      target: 'recent-sales-orders' as WorkspaceCardId,
      warning: false,
    },
  ].filter(Boolean) as Array<{
    id: string;
    label: string;
    value: string | null;
    hint: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: MetricTone;
    target: WorkspaceCardId;
    warning: boolean;
  }>;

  const localMetric = (value: number | undefined, loading: boolean, error: boolean, hint: string) => ({
    value: loading ? null : error || value === undefined ? 'Unavailable' : value.toLocaleString(),
    hint, warning: false,
  });
  const extraLocalValues: Record<string, { value: string | null; hint: string; warning: boolean }> = {
    'pending-orders': localMetric(pendingOrders, ordersLoading, ordersError, 'Awaiting processing'),
    'average-order-value': {
      value: ordersLoading ? null : ordersError || !orderKpis ? 'Unavailable' : formatCurrency(orderKpis.averageOrderValue),
      hint: 'Booked sales orders', warning: false,
    },
    'stockouts': localMetric(inventoryKpis?.itemsOutOfStock, inventoryLoading, inventoryError, 'Current stockouts'),
    'low-stock': localMetric(inventoryKpis?.itemsBelowReorderPoint, inventoryLoading, inventoryError, 'Current replenishment needs'),
    'available-units': localMetric(inventoryKpis?.totalQuantityAvailable, inventoryLoading, inventoryError, 'Available for allocation'),
    'reserved-units': localMetric(inventoryKpis?.totalQuantityReserved, inventoryLoading, inventoryError, 'Reserved for demand'),
    'delivered-shipments': localMetric(shipmentKpis?.deliveredCount, shipmentLoading, shipmentError, 'All delivered shipments'),
    'held-shipments': localMetric(shipmentKpis?.onHoldCount, shipmentLoading, shipmentError, 'Current holds'),
    'invoice-approvals': localMetric(invoiceOverview?.counts.pendingApprovals, invoiceOverviewLoading, invoiceOverviewError, 'Current approval queue'),
    'invoice-exceptions': localMetric(invoiceOverview?.counts.auditExceptions, invoiceOverviewLoading, invoiceOverviewError, 'Current audit exceptions'),
    'unreconciled-payments': localMetric(invoiceOverview?.counts.unreconciledPayments, invoiceOverviewLoading, invoiceOverviewError, 'Awaiting reconciliation'),
    'overdue-invoices': localMetric(invoiceOverview?.counts.overdue, invoiceOverviewLoading, invoiceOverviewError, 'Current overdue invoices'),
  };
  const selectedAnalyticsKpis = availableKpiDefinitions.filter((item) =>
    (item.measure || item.visualId) && draftLayout.orderedKpiIds.includes(item.id)
  );
  const analyticsKpiQueries = useQueries({
    queries: selectedAnalyticsKpis.map((item) => ({
      queryKey: ['workspace', 'kpi', user?.id, activeCompanyId, item.id],
      enabled: layoutReady && Boolean(user?.id && activeCompanyId) && (!item.visualId || !!customKpiMetadata[customKpiIds.indexOf(item.id)]?.data),
      staleTime: 60_000,
      retry: false,
      queryFn: async () => {
        if (item.visualId) {
          const batch = await executeVisualBatch([{ visualId: item.visualId }]);
          const result = batch.results[0];
          if (!result?.ok || !result.result) throw new Error(result?.error ?? 'The KPI could not be loaded.');
          return result.result;
        }
        return executeAnalysis({
        title: item.label, view: item.measure!.split('.')[0], measures: [item.measure!],
        dimensions: [], filters: [], template: 'horizontal_bar', drillPath: [],
        });
      },
    })),
  });
  const focusItems = draftLayout.orderedKpiIds.flatMap((id) => {
    const definition = availableKpiDefinitions.find((item) => item.id === id);
    if (!definition) return [];
    const local = localFocusItems.find((item) => item.id === id) ?? extraLocalValues[id];
    if (local) return [{ ...definition, value: local.value, hint: local.hint, warning: local.warning }];
    const query = analyticsKpiQueries[selectedAnalyticsKpis.findIndex((item) => item.id === id)];
    const numeric = query?.data?.visual.series[0]?.points[0]?.value;
    const metadataFailed = !!definition.visualId && customKpiMetadata[customKpiIds.indexOf(id)]?.isError;
    const value = metadataFailed ? 'Unavailable' : query?.isPending ? null : query?.isError ? 'Unavailable' :
      typeof numeric !== 'number' || !Number.isFinite(numeric) ? 'No data' :
      definition.visualId ? formatMetric(numeric, query?.data?.visual.series[0]?.unit ?? 'number') :
      definition.unit === 'percent' ? `${(numeric * 100).toFixed(1)}%` :
      definition.unit === 'days' ? `${numeric.toFixed(1)} d` :
      numeric.toLocaleString(undefined, { maximumFractionDigits: definition.unit === 'count' ? 0 : 1 });
    return [{ ...definition, value, hint: query?.isError ? widgetFailureHint(query.error) : definition.description, warning: false }];
  });

  const renderCardBody = (id: WorkspaceCardId) => {
    // Chart cards are dynamic, so they are matched before the static switch.
    if (isVisualCardId(id)) {
      const visualId = visualIdFromCardId(id);
      return (
        <WorkspaceChartCard
          visualId={visualId}
          onContent={reportCardContent}
          presentation={id.startsWith('card:') ? 'table' : 'chart'}
          onTitle={(title) =>
            setChartTitles((current) =>
              current[visualId] === title ? current : { ...current, [visualId]: title }
            )
          }
        />
      );
    }

    switch (id) {
      case 'shipment-calendar':
        return <ShipmentCalendarCard />;
      case 'expected-arrivals':
        return <ExpectedArrivalsCard onContent={reportCardContent} />;
      case 'radar':
        return (
          <div className="flex h-full min-h-0 flex-col">
            {/* The radar hides the shell header, so it carries its own module
                eyebrow — and it links out the same way every other card's does. */}
            <div className="shrink-0 px-4 pb-1 pt-3">
              <CardModuleLink module="Operations" href="/shipments/radar" editing={editing} />
            </div>
            <div className="min-h-0 flex-1">
              <WorkspaceRadarBody onContent={reportCardContent} />
            </div>
          </div>
        );

      case 'collaboration':
        return <CollaborationCard onContent={reportCardContent} />;

      case 'invoice-inbox':
        if (invoiceOverviewLoading) return <CardSkeleton />;
        if (invoiceOverviewError || !invoiceOverview)
          return (
            <ErrorState
              message="Invoice work queues could not be loaded."
              error={invoiceOverviewFailure}
            />
          );
        return (
          <CardContent className="space-y-4 p-4">
            <Link
              to="/invoices/incoming"
              className="flex items-center justify-between rounded-md p-2 hover:bg-muted"
            >
              <span className="text-sm">Incoming invoices</span>
              <strong className="tabular-nums">{invoiceOverview.counts.incoming}</strong>
            </Link>
            <Link
              to="/invoices/exceptions"
              className="flex items-center justify-between rounded-md p-2 hover:bg-muted"
            >
              <span className="text-sm">Audit blockers</span>
              <strong
                className={invoiceOverview.counts.auditExceptions ? 'text-warning-foreground' : ''}
              >
                {invoiceOverview.counts.auditExceptions}
              </strong>
            </Link>
            <Link
              to="/invoices/exceptions?queue=approvals"
              className="flex items-center justify-between rounded-md p-2 hover:bg-muted"
            >
              <span className="text-sm">Pending approvals</span>
              <strong className="tabular-nums">{invoiceOverview.counts.pendingApprovals}</strong>
            </Link>
          </CardContent>
        );

      case 'invoice-cash':
        if (invoiceOverviewLoading) return <CardSkeleton />;
        if (invoiceOverviewError || !invoiceOverview)
          return (
            <ErrorState
              message="Invoice due-state data could not be loaded."
              error={invoiceOverviewFailure}
            />
          );
        return (
          <CardContent className="space-y-4 p-4">
            <Link to="/invoices/payables" className="block rounded-md p-3 hover:bg-muted">
              <div className="text-xs text-muted-foreground">Payables due within 7 days</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {invoiceOverview.counts.duePayables}
              </div>
            </Link>
            <Link to="/invoices/receivables" className="block rounded-md p-3 hover:bg-muted">
              <div className="text-xs text-muted-foreground">Overdue receivables</div>
              <div
                className={`mt-1 text-2xl font-semibold tabular-nums ${invoiceOverview.counts.overdueReceivables ? 'text-warning-foreground' : ''}`}
              >
                {invoiceOverview.counts.overdueReceivables}
              </div>
            </Link>
          </CardContent>
        );

      case 'invoice-payments':
        if (invoiceOverviewLoading) return <CardSkeleton />;
        if (invoiceOverviewError || !invoiceOverview)
          return (
            <ErrorState
              message="Payment reconciliation queues could not be loaded."
              error={invoiceOverviewFailure}
            />
          );
        return (
          <CardContent className="space-y-4 p-4">
            <Link
              to="/invoices/payments?direction=incoming"
              className="block rounded-md p-3 hover:bg-muted"
            >
              <div className="text-xs text-muted-foreground">Unapplied receipts</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {invoiceOverview.counts.unappliedReceipts}
              </div>
            </Link>
            <Link to="/invoices/payments" className="block rounded-md p-3 hover:bg-muted">
              <div className="text-xs text-muted-foreground">Unreconciled payments</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {invoiceOverview.counts.unreconciledPayments}
              </div>
            </Link>
          </CardContent>
        );

      case 'inventory-health':
        if (inventoryLoading) return <CardSkeleton />;
        if (inventoryError || !inventoryKpis) {
          return (
            <ErrorState message="Inventory health could not be loaded. Existing values are not shown as zero." error={inventoryFailure} />
          );
        }
        return (
          <CardContent className="space-y-3 p-4">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <MiniMetric
                label="Below reorder"
                value={inventoryKpis.itemsBelowReorderPoint.toLocaleString()}
                tone={inventoryKpis.itemsBelowReorderPoint > 0 ? 'warning' : 'neutral'}
                askPrompt={`Investigate the ${inventoryKpis.itemsBelowReorderPoint.toLocaleString()} SKUs below reorder point. Rank them by operational risk and recommend which ones need action first.`}
              />
              <MiniMetric
                label="Out of stock"
                value={inventoryKpis.itemsOutOfStock.toLocaleString()}
                tone={inventoryKpis.itemsOutOfStock > 0 ? 'warning' : 'neutral'}
                askPrompt={`Investigate the ${inventoryKpis.itemsOutOfStock.toLocaleString()} out-of-stock SKUs. Explain affected demand, customers, and inbound supply.`}
              />
              <MiniMetric
                label="Available units"
                value={inventoryKpis.totalQuantityAvailable.toLocaleString()}
                tone="primary"
                askPrompt={`Explain our ${inventoryKpis.totalQuantityAvailable.toLocaleString()} available inventory units. Which SKUs and locations drive this number, and what needs attention?`}
              />
              <MiniMetric
                label="Reserved units"
                value={inventoryKpis.totalQuantityReserved.toLocaleString()}
                askPrompt={`Explain why ${inventoryKpis.totalQuantityReserved.toLocaleString()} inventory units are reserved. Show the orders, SKUs, and customers creating the largest reservations.`}
              />
            </div>
            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">Inventory value</p>
                <AskLineItemButton
                  label="inventory value"
                  prompt={`Explain the current inventory value of ${formatCurrency(inventoryKpis.totalValue)}. Show which products and warehouses hold the most value and identify slow-moving or exposed stock.`}
                />
              </div>
              <p className="mt-1 text-lg font-semibold tabular-nums">
                {formatCurrency(inventoryKpis.totalValue)}
              </p>
            </div>
            <div className="-mx-4 -mb-4 mt-1 border-t border-border px-4 py-2.5">
              <Link to="/inventory" className="text-xs font-medium text-primary hover:underline">
                View all inventory
              </Link>
            </div>
          </CardContent>
        );

      case 'recent-sales-orders':
        return (
          <CardContent className="p-0">
            {salesOrdersLoading ? (
              <CardSkeleton />
            ) : salesOrdersError ? (
              <ErrorState message="Orders needing attention could not be loaded." error={salesOrdersFailure} />
            ) : salesOrders.length === 0 ? (
              <EmptyState
                compact
                title="No open orders need attention"
                description="Open orders appear here, with overdue deliveries and backorders first."
              />
            ) : (
              <ul className="divide-y divide-border">
                {salesOrders.map(({ order, reasons }) => (
                  <li
                    key={order.id}
                    className="flex items-center transition-colors hover:bg-muted/60"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(buildSalesOrderDetailUrl(order.id))}
                      className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 pr-2 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{order.orderNumber}</span>
                          <SalesOrderStatusBadge status={order.status} />
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {order.customerName}
                        </p>
                        <p className="mt-1 text-[11px] text-warning-foreground">
                          {reasons.join(' · ')}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold tabular-nums">
                          {formatCurrency(order.total, order.currency)}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {formatDate(order.orderDate)}
                        </p>
                      </div>
                    </button>
                    <AskLineItemButton
                      className="mr-3"
                      label={`sales order ${order.orderNumber}`}
                      prompt={`Analyze sales order ${order.orderNumber}. Use the live order, stock, fulfillment, and backorder records to explain its current status, blockers, risks, and next best action.`}
                      pageContext={{
                        pluginId: 'orders',
                        entityType: 'salesOrder',
                        entityId: order.id,
                        url: buildSalesOrderDetailUrl(order.id),
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-border px-4 py-2.5">
              <p className="mb-1 text-[11px] text-muted-foreground">
                Prioritized from the latest 100 orders · up to 3 shown
              </p>
              <Link to="/orders/sales" className="text-xs font-medium text-primary hover:underline">
                View all sales orders
              </Link>
            </div>
          </CardContent>
        );

      case 'recent-purchase-orders':
        return (
          <CardContent className="p-0">
            {purchaseOrdersLoading ? (
              <CardSkeleton />
            ) : purchaseOrdersError ? (
              <ErrorState message="Recent purchase orders could not be loaded." error={purchaseOrdersFailure} />
            ) : purchaseOrders.length === 0 ? (
              <EmptyState
                compact
                title="No purchase orders yet"
                description="Recent purchase orders will appear here."
              />
            ) : (
              <ul className="divide-y divide-border">
                {purchaseOrders.map((order) => (
                  <li
                    key={order.id}
                    className="flex items-center transition-colors hover:bg-muted/60"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(buildPurchaseOrderDetailUrl(order.id))}
                      className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 pr-2 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{order.orderNumber}</span>
                          <PurchaseOrderStatusBadge status={order.status} />
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {order.supplierName}
                          {order.warehouseName ? ` · ${order.warehouseName}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold tabular-nums">
                          {formatCurrency(order.total, order.currency)}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {order.expectedDeliveryDate
                            ? formatDate(order.expectedDeliveryDate)
                            : 'No ETA'}
                        </p>
                      </div>
                    </button>
                    <AskLineItemButton
                      className="mr-3"
                      label={`purchase order ${order.orderNumber}`}
                      prompt={`Analyze purchase order ${order.orderNumber}. Use the live purchase order and line-item records to explain receipt progress, delay risk, affected demand where supported, and the next supplier action.`}
                      pageContext={{
                        pluginId: 'orders',
                        entityType: 'purchaseOrder',
                        entityId: order.id,
                        url: buildPurchaseOrderDetailUrl(order.id),
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-border px-4 py-2.5">
              <Link
                to="/orders/purchase"
                className="text-xs font-medium text-primary hover:underline"
              >
                View all purchase orders
              </Link>
            </div>
          </CardContent>
        );

      case 'purchase-orders-at-risk':
        return (
          <CardContent className="p-0">
            {purchaseOrderRiskLoading || (canViewBackorders && backordersLoading) ? (
              <CardSkeleton />
            ) : purchaseOrderRiskError ? (
              <ErrorState message="Purchase-order risk could not be loaded. Existing orders are not shown as safe." error={purchaseOrderRiskFailure} />
            ) : purchaseOrdersAtRisk.length === 0 ? (
              <EmptyState
                compact
                title="No open purchase-order risks"
                description="No overdue, missing-ETA, high-value, or demand-linked purchase orders were found."
              />
            ) : (
              <ul className="divide-y divide-border">
                {purchaseOrdersAtRisk.map(({ order, risks }) => (
                  <li
                    key={order.id}
                    className="flex items-center transition-colors hover:bg-muted/60"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(buildPurchaseOrderDetailUrl(order.id))}
                      className="min-w-0 flex-1 py-3 pl-4 pr-2 text-left"
                    >
                      <div className="flex min-w-0 items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{order.orderNumber}</p>
                          <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {order.supplierName}
                          </p>
                        </div>
                        <p className="shrink-0 text-sm font-semibold tabular-nums">
                          {formatCurrency(order.total, order.currency)}
                        </p>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {risks.map((risk) => (
                          <Badge
                            key={risk.key}
                            tone={
                              risk.key === 'blocked-demand' || risk.key === 'overdue'
                                ? 'warning'
                                : 'neutral'
                            }
                            variant="subtle"
                            size="sm"
                          >
                            {risk.label}
                          </Badge>
                        ))}
                      </div>
                    </button>
                    <AskLineItemButton
                      className="mr-3"
                      label={`purchase-order risk ${order.orderNumber}`}
                      prompt={`Analyze why purchase order ${order.orderNumber} is at risk. Its current signals are ${risks.map((risk) => risk.label).join(', ')}. Explain the supplier, ETA, value, blocked customer demand, and next best action using live records.`}
                      pageContext={{
                        pluginId: 'orders',
                        entityType: 'purchaseOrder',
                        entityId: order.id,
                        url: buildPurchaseOrderDetailUrl(order.id),
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            <div className="space-y-1 border-t border-border px-4 py-2.5">
              <p className="text-xs text-muted-foreground">
                High value is the top 20% of open PO value.
              </p>
              {canViewBackorders ? (
                backordersError && (
                  <p className="text-xs text-warning-foreground">
                    Blocked-demand links are temporarily unavailable.
                  </p>
                )
              ) : (
                <p className="text-xs text-muted-foreground">
                  Backorder access is required to assess blocked demand.
                </p>
              )}
            </div>
          </CardContent>
        );

      case 'shipment-flow':
        if (shipmentLoading) return <CardSkeleton />;
        if (shipmentError || !shipmentKpis) {
          return (
            <ErrorState message="Shipment status could not be loaded. Existing values are not shown as zero." error={shipmentFailure} />
          );
        }
        return (
          <CardContent className="space-y-3 p-4">
            <Link
              to="/shipments?status=on_hold"
              className="flex items-center justify-between gap-2 rounded-md bg-warning-subtle px-3 py-2 text-xs text-warning-foreground"
            >
              <span>{shipmentKpis.onHoldCount > 0 ? 'Resolve shipment holds' : 'No shipments on hold'}</span>
              <span className="font-semibold tabular-nums">{shipmentKpis.onHoldCount}</span>
            </Link>
            <div className="grid grid-cols-2 gap-3">
              <MiniMetric
                label="On hold"
                value={shipmentKpis.onHoldCount.toLocaleString()}
                tone={shipmentKpis.onHoldCount > 0 ? 'warning' : 'neutral'}
                askPrompt={`Investigate the ${shipmentKpis.onHoldCount.toLocaleString()} shipments on hold. Explain each blocker, business impact, and recommended resolution order.`}
              />
              <MiniMetric
                label="Arrived"
                value={shipmentKpis.arrivedCount.toLocaleString()}
                tone="success"
                askPrompt={`Review the ${shipmentKpis.arrivedCount.toLocaleString()} arrived shipments. Show which still need customs, warehouse receipt, or final delivery action.`}
              />
              <MiniMetric
                label="In transit"
                value={shipmentKpis.inTransitCount.toLocaleString()}
                tone="primary"
                askPrompt={`Review the ${shipmentKpis.inTransitCount.toLocaleString()} shipments currently in transit. Identify late, high-priority, or customer-critical shipments and explain what needs a decision.`}
              />
              <MiniMetric
                label="Booked"
                value={shipmentKpis.bookedCount.toLocaleString()}
                askPrompt={`Review the ${shipmentKpis.bookedCount.toLocaleString()} booked shipments. Identify departures at risk, missing milestones, and the next operational actions.`}
              />
            </div>
            <div className="-mx-4 -mb-4 mt-1 border-t border-border px-4 py-2.5">
              <Link to="/shipments" className="text-xs font-medium text-primary hover:underline">
                View all shipments
              </Link>
            </div>
          </CardContent>
        );

      case 'warehouse-capacity':
        if (warehouseLoading) return <CardSkeleton />;
        if (warehouseError || !warehouseKpis) {
          return (
            <ErrorState message="Warehouse capacity could not be loaded. Existing values are not shown as zero." />
          );
        }
        return (
          <CardContent className="space-y-5 p-4">
            <div className="grid grid-cols-2 gap-5">
              <MiniMetric
                label="Active warehouses"
                value={`${warehouseKpis.activeWarehouses} / ${warehouseKpis.totalWarehouses}`}
                tone="primary"
                askPrompt={`Review our ${warehouseKpis.activeWarehouses} active warehouses out of ${warehouseKpis.totalWarehouses}. Explain inactive locations, workload concentration, and operational risks.`}
              />
              <MiniMetric
                label="Floor area"
                value={`${warehouseSquareFeet.toLocaleString()} sq ft`}
                askPrompt={`We have ${warehouseSquareFeet.toLocaleString()} square feet of warehouse floor area across ${warehouseKpis.totalWarehouses} sites. Compare the sites by size and flag where capacity is concentrated.`}
              />
              <MiniMetric
                label="Pallet positions"
                value={warehousePallets.toLocaleString()}
                askPrompt={`We have ${warehousePallets.toLocaleString()} pallet positions across our warehouses. Break that down by site and identify where it is concentrated.`}
              />
              <MiniMetric
                label="Volume"
                value={`${warehouseCubicFeet.toLocaleString()} cu ft`}
                askPrompt={`We have ${warehouseCubicFeet.toLocaleString()} cubic feet of warehouse volume. Break it down by site and compare it with floor area.`}
              />
            </div>
            <div className="-mx-4 -mb-4 mt-1 border-t border-border px-4 py-2.5">
              <Link to="/warehouse" className="text-xs font-medium text-primary hover:underline">
                View all warehouses
              </Link>
            </div>
          </CardContent>
        );

      case 'warehouse-task-aging':
        if (warehouseTasksLoading) return <CardSkeleton />;
        if (warehouseTasksError || !warehouseTasksData) {
          return (
            <ErrorState message="Warehouse task aging could not be loaded. Existing tasks are not shown as current." />
          );
        }
        return (
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {warehouseTaskAgingRows.map((row) => (
                <li key={row.type} className="flex min-h-[58px] items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{row.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {row.count === 0
                        ? 'No overdue open tasks'
                        : `Oldest open task: ${row.oldestDays} days`}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-lg font-semibold tabular-nums ${
                      row.count > 0 ? 'text-warning-foreground' : 'text-foreground'
                    }`}
                  >
                    {row.count}
                  </span>
                  <AskLineItemButton
                    label={`overdue ${row.type === 'transfer' ? 'transfer' : row.label.toLowerCase()} tasks`}
                    prompt={`Investigate the ${row.count} overdue ${row.type === 'transfer' ? 'transfer' : row.label.toLowerCase()} warehouse tasks. Explain the oldest tasks, warehouses, owners, blocked work, and recommended action order using live task records.`}
                  />
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5">
              <p className="text-xs text-muted-foreground">
                Overdue means open for more than {WAREHOUSE_TASK_OVERDUE_DAYS} days.
              </p>
              <Link
                to="/warehouse/tasks"
                className="shrink-0 text-xs font-medium text-primary hover:underline"
              >
                View all tasks
              </Link>
            </div>
          </CardContent>
        );
    }
  };

  const orderedDefinitions = draftLayout.orderedCardIds
    .map((id) => definitionById.get(id))
    .filter(Boolean) as WorkspaceCardDefinition[];
  const optimizeLayout = async () => {
    if (optimizing || !draftLayout.orderedCardIds.length) return;
    const request = ++optimizeSequence.current;
    const snapshot = draftLayout;
    const state = (loading: boolean, error: boolean, count?: number): WorkspaceCardContent['state'] => error ? 'error' : loading ? 'loading' : count === undefined ? 'unknown' : count ? 'populated' : 'empty';
    const localContent: Record<string, WorkspaceCardContent> = {
      insights: { state: insightsEmpty ? 'empty' : 'populated' },
      'inventory-health': { state: state(inventoryLoading, inventoryError, inventoryKpis?.totalItems), metricCount: 5, attention: inventoryKpis?.itemsBelowReorderPoint ? 'high' : 'normal' },
      'recent-sales-orders': { state: state(salesOrdersLoading, salesOrdersError, salesOrdersData ? salesOrders.length : undefined), itemCount: salesOrders.length, attention: salesOrders.length ? 'high' : 'normal' },
      'recent-purchase-orders': { state: state(purchaseOrdersLoading, purchaseOrdersError, purchaseOrdersData ? purchaseOrders.length : undefined), itemCount: purchaseOrders.length },
      'purchase-orders-at-risk': { state: state(purchaseOrderRiskLoading, purchaseOrderRiskError, purchaseOrderRiskData ? purchaseOrdersAtRisk.length : undefined), itemCount: purchaseOrdersAtRisk.length, attention: purchaseOrdersAtRisk.length ? 'high' : 'normal' },
      'shipment-flow': { state: state(shipmentLoading, shipmentError, shipmentKpis?.totalShipments), metricCount: 4, attention: shipmentKpis?.onHoldCount ? 'high' : 'normal' },
      'warehouse-capacity': { state: state(warehouseLoading, warehouseError, warehouseKpis?.totalWarehouses), metricCount: 4 },
      'warehouse-task-aging': { state: state(warehouseTasksLoading, warehouseTasksError, warehouseTasksData ? 4 : undefined), itemCount: 4, attention: warehouseTaskAgingRows.some((row) => row.count > 0) ? 'high' : 'normal' },
      ...Object.fromEntries(['invoice-inbox', 'invoice-cash', 'invoice-payments'].map((id) => [id, { state: state(invoiceOverviewLoading, invoiceOverviewError, invoiceOverview ? 1 : undefined), metricCount: id === 'invoice-inbox' ? 3 : 2, attention: invoiceOverview?.counts.auditExceptions || invoiceOverview?.counts.overdueReceivables || invoiceOverview?.counts.unreconciledPayments ? 'high' : 'normal' } satisfies WorkspaceCardContent])),
    };
    const cards: WorkspaceCardContext[] = orderedDefinitions.map((card) => ({
      id: card.id, title: card.title, module: card.module,
      kind: card.id === 'insights' ? 'insights' : card.id === 'expected-arrivals' ? 'calendar' : isChartCardId(card.id) ? 'chart' : 'card',
      content: localContent[card.id] ?? cardContent[card.id] ?? { state: 'unknown' },
    }));
    setOptimizing(true); setOptimizationError(null); setOptimizationNote(null);
    try {
      const proposal = await optimizeWorkspaceLayout(serializeWorkspaceLayout(snapshot), cards);
      if (optimizeSequence.current !== request) return;
      if (!workspaceLayoutsEqual(latestDraft.current, snapshot)) {
        setOptimizationNote('Your layout changed while AI was working. Optimize again to include your latest edits.');
        return;
      }
      setDraftLayout((current) => ({ ...current, orderedCardIds: proposal.layout.orderedCardIds as WorkspaceCardId[], cardLayouts: proposal.layout.cardLayouts }));
      setOptimizationNote(`${proposal.explanation} Review the arrangement, then Save to keep it.`);
      setSaveError(null);
    } catch {
      if (optimizeSequence.current === request) setOptimizationError('AI could not optimize this workspace right now. Your layout has been kept. Please try again.');
    } finally { if (optimizeSequence.current === request) setOptimizing(false); }
  };
  const aiData = {
    sales: (orderKpis?.totalOrders ?? 0) > 0 && canUseAiView('sales_performance'),
    inventory: (inventoryKpis?.totalItems ?? 0) > 0 && canUseAiView('inventory_position'),
    purchaseOrders: (purchaseOrderRiskData?.items?.length ?? purchaseOrdersData?.items?.length ?? 0) > 0 && canUseAiView('supplier_delay_risk'),
    shipments: (shipmentKpis?.totalShipments ?? 0) > 0 && canUseAiView('shipment_operations'),
  };
  const aiOptions = {
    canCreate: canAddCharts && hasPermission('analytics:visuals:write'), canUseView: canUseAiView, onCreated: addAiWidget,
    starters: { chart: workspaceAiStarters(aiData, 'chart'), kpi: workspaceAiStarters(aiData, 'kpi'), card: workspaceAiStarters(aiData, 'card') },
  };
  const compactInsights = insightsEmpty && !editing;
  const responsiveCardLayouts = useMemo(
    () =>
      ({
        lg: draftLayout.cardLayouts.lg.map((item) => ({
          ...item,
          h: item.i === 'insights' && compactInsights ? emptyInsightsRows : item.h,
          minW: 2,
          minH: item.i === 'insights' ? 1 : 6,
          maxH: 20,
        })),
        md: draftLayout.cardLayouts.md.map((item) => ({
          ...item,
          h: item.i === 'insights' && compactInsights ? emptyInsightsRows : item.h,
          minW: 1,
          minH: item.i === 'insights' ? 1 : 6,
          maxH: 20,
        })),
        sm: draftLayout.cardLayouts.sm.map((item) => ({
          ...item,
          h: item.i === 'insights' && compactInsights ? emptyInsightsRows : item.h,
          minW: 1,
          maxW: 1,
          minH: item.i === 'insights' ? 1 : 6,
          maxH: 20,
        })),
      }) satisfies ResponsiveLayouts<WorkspaceBreakpoint>,
    [draftLayout.cardLayouts, compactInsights, emptyInsightsRows]
  );

  return (
    <>
      <PageHeader
        className="sticky top-0 z-10 -mx-6 -mt-6 bg-background px-6 pt-3"
        title="My Workspace"
        actions={
          <div className="flex w-full flex-wrap items-center justify-end gap-2 md:w-auto">
            {!editing && canViewGlobalInsights && (
              <a
                href="https://proscs.lognai.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={buttonStyles({
                  variant: 'secondary',
                  size: 'sm',
                  className: 'w-full justify-center whitespace-nowrap sm:w-auto',
                })}
              >
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>Supply Chain Sentinel</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
              </a>
            )}
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setConfirmingReset(true)}>
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </Button>
                <DropdownMenu>
                  <DropdownMenu.Trigger asChild>
                    <Button variant="secondary" size="sm">Starting layout</Button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end">
                    {WORKSPACE_PRESETS.map((preset) => (
                      <DropdownMenu.Item key={preset.id} onSelect={() => applyPreset(preset.id)}>
                        {preset.label}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu>
                <Button variant="secondary" size="sm" onClick={cancelEditing}>
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={saveLayout}
                  isLoading={savePreferences.isPending}
                  disabled={!unsaved || optimizing}
                >
                  <Save className="h-4 w-4" />
                  Save
                </Button>
              </>
            ) : (
              <IconButton
                icon={Pencil}
                aria-label="Customize layout"
                variant="primary"
                size="sm"
                onClick={() => {
                  // Start from the layout currently on screen, including an
                  // empty Insights card's natural height and compacted cards.
                  setDraftLayout((current) => applyWorkspaceGridLayouts(current, {
                    lg: verticalCompactor.compact(responsiveCardLayouts.lg, WORKSPACE_COLUMNS.lg),
                    md: verticalCompactor.compact(responsiveCardLayouts.md, WORKSPACE_COLUMNS.md),
                    sm: verticalCompactor.compact(responsiveCardLayouts.sm, WORKSPACE_COLUMNS.sm),
                  }, activeBreakpoint));
                  setEditing(true);
                  setSaveError(null);
                }}
                disabled={mobile || !activeCompanyId}
                title={
                  mobile ? 'Layout editing is available on tablet and desktop' : 'Customize layout'
                }
              />
            )}
          </div>
        }
      />

      <PageLayout className="space-y-5 pt-4">
        {focusItems.length > 0 && (
          <MetricRibbon
            role="region"
            aria-label="Operational focus"
            items={focusItems.map((item, index) => {
              const Icon = item.icon;
              return {
                id: item.id,
                label: item.label,
                icon: <Icon />,
                tone: item.tone,
                // A null value means that KPI's query is still in flight; the
                // strip as a whole is not loading, so skeleton just this cell.
                value:
                  item.value === null ? (
                    <span className="block h-6 w-20 animate-pulse rounded bg-muted" />
                  ) : (
                    item.value
                  ),
                hint: item.warning ? (
                  <span className="text-warning-foreground">{item.hint}</span>
                ) : (
                  item.hint
                ),
                onClick: editing ? undefined : () => navigate(item.href),
                actions: editing && !mobile ? (
                  <DropdownMenu>
                    <DropdownMenu.Trigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 px-0" aria-label={`Arrange ${item.label} KPI`}>
                        <EllipsisVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                      <DropdownMenu.Item disabled={index === 0} onSelect={() => moveKpi(item.id, -1)}>
                        <ArrowUp className="h-4 w-4" /> Move earlier
                      </DropdownMenu.Item>
                      <DropdownMenu.Item disabled={index === focusItems.length - 1} onSelect={() => moveKpi(item.id, 1)}>
                        <ArrowDown className="h-4 w-4" /> Move later
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator />
                      <DropdownMenu.Item className="text-destructive" onSelect={() => removeKpi(item.id)}>
                        <X className="h-4 w-4" /> Remove KPI
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu>
                ) : undefined,
              };
            })}
          />
        )}

        {/*
          One explanation for the whole page. A dozen widgets failing at the
          same instant almost always share one cause, and the reader needs that
          cause and a way back — not twelve copies of "Data unavailable".
        */}
        {loadFailure.kind !== 'none' && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning-subtle px-4 py-3"
          >
            <div className="flex min-w-0 items-start gap-3">
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning-foreground" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{loadFailure.title}</p>
                <p className="text-xs text-muted-foreground">{loadFailure.detail}</p>
              </div>
            </div>
            {loadFailure.retryable && (
              <Button variant="secondary" size="sm" onClick={retryFailedPanels} disabled={retrying}>
                <RefreshCw className="h-4 w-4" />
                {retrying ? 'Retrying…' : 'Try again'}
              </Button>
            )}
          </div>
        )}

        {/*
          Suppressed while the page-level banner is already explaining the same
          transient cause — two stacked warnings for one event is the noise this
          is meant to remove. A save failure is a different action and always
          shows.
        */}
        {(saveError || (loadFailed && !loadFailure.retryable)) && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning-subtle px-4 py-3"
          >
            <div className="flex min-w-0 items-start gap-3">
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {saveError ? 'Layout could not be saved' : 'Saved layout could not be loaded'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {saveError ??
                    'The default layout is shown as unsaved. Your server preference was not replaced.'}
                </p>
              </div>
            </div>
            {loadFailed && !saveError && (
              <Button variant="secondary" size="sm" onClick={() => preferenceQuery.refetch()}>
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            )}
            {saveError && (
              <Button variant="secondary" size="sm" onClick={saveLayout}>
                <RefreshCw className="h-4 w-4" />
                Retry save
              </Button>
            )}
          </div>
        )}

        <WorkspaceKpiPicker
          open={kpiPickerOpen}
          onOpenChange={setKpiPickerOpen}
          definitions={availableKpiDefinitions}
          alreadyAdded={draftLayout.orderedKpiIds}
          onSelect={addKpi}
          ai={aiOptions}
        />

        <WorkspaceCardPicker
          open={cardPickerOpen}
          startWithAi={cardPickerAi}
          onOpenChange={setCardPickerOpen}
          definitions={visibleDefinitions}
          alreadyAdded={draftLayout.orderedCardIds}
          onSelect={addCard}
          ai={aiOptions}
        />

        <ChartCardPicker
          open={chartPickerOpen}
          onOpenChange={setChartPickerOpen}
          onSelect={addChartCard}
          ai={aiOptions}
          alreadyAdded={
            new Set(draftLayout.orderedCardIds.filter(isChartCardId).map(visualIdFromCardId))
          }
        />

        {!layoutReady ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
            {Array.from({ length: Math.max(visibleDefinitions.length, 3) }).map((_, index) => (
              <Card key={index} className="min-h-56 md:col-span-1 xl:col-span-2">
                <CardSkeleton />
              </Card>
            ))}
          </div>
        ) : (
          <>
          {orderedDefinitions.every((definition) => definition.id === 'insights') && (
          <Card>
            <CardContent>
              {/*
                An empty board has two very different causes and this asserted
                the wrong one. "Your current company and permissions do not
                expose any Workspace modules" is a claim about entitlement,
                and it was rendered whenever the board came up empty — including
                when every panel had just failed on a 429, directly beneath the
                banner correctly reporting that the requests had not completed.
                The page contradicted itself, and the half a reader believes is
                the one that names them: they open a support ticket about
                permissions they already have (QA ledger ASK2-001).

                `loadFailure` already knows which it is, so it decides.
              */}
              {loadFailure.kind !== 'none' ? (
                <EmptyState
                  title={loadFailure.title}
                  description={`${loadFailure.detail} Nothing is missing from your access — the board is empty because the panels could not be loaded.`}
                  action={
                    loadFailure.retryable ? (
                      <Button variant="outline" onClick={retryFailedPanels} disabled={retrying}>
                        {retrying ? 'Retrying…' : 'Try again'}
                      </Button>
                    ) : undefined
                  }
                />
              ) : visibleDefinitions.length > 0 || canAddCharts ? (
                <EmptyState
                  title="Your workspace has no cards"
                  description="Customize your layout to add cards and charts."
                />
              ) : (
                <EmptyState
                  title="No operational cards available"
                  description="Your current company and permissions do not expose any Workspace modules."
                />
              )}
            </CardContent>
          </Card>
          )}
          <section
            ref={gridContainerRef}
            aria-label="Workspace briefing board"
            className={`relative min-w-0 ${editing && !mobile ? 'workspace-grid--editing overflow-visible' : 'overflow-hidden'}`}
          >
            {editing && !mobile && <WorkspaceInsertSlot label="Insert before the first card" className="workspace-insert-card--before" canAddCharts={canAddCharts} onChoose={(kind) => {
              setInsertionIndex(0);
              if (kind === 'card') openCardPicker();
              else if (kind === 'chart') setChartPickerOpen(true);
              else setKpiPickerOpen(true);
            }} />}
            {gridWidth > 0 && (
              <Responsive
                className={
                  editing && !mobile ? 'workspace-grid workspace-grid--editing' : 'workspace-grid'
                }
                width={gridWidth}
                layouts={responsiveCardLayouts}
                breakpoints={WORKSPACE_BREAKPOINTS}
                cols={WORKSPACE_COLUMNS}
                rowHeight={40}
                margin={[12, 12]}
                containerPadding={[0, 0]}
                compactor={verticalCompactor}
                dragConfig={{
                  enabled: editing && !mobile,
                  bounded: true,
                  handle: '.workspace-drag-handle',
                  cancel: 'button,input,select,textarea,a,[role="menu"]',
                  threshold: 3,
                }}
                resizeConfig={{
                  enabled: editing && !mobile,
                  handles: ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se'],
                }}
                onBreakpointChange={(breakpoint) => {
                  setActiveBreakpoint(breakpoint as WorkspaceBreakpoint);
                }}
                onLayoutChange={(_layout, nextLayouts) => {
                  if (!editing || mobile) return;
                  setDraftLayout((current) =>
                    applyWorkspaceGridLayouts(
                      current,
                      nextLayouts as ResponsiveLayouts<WorkspaceBreakpoint>,
                      activeBreakpoint
                    )
                  );
                  setSaveError(null);
                }}
              >
                {orderedDefinitions.map((definition, index) => (
                  <div key={definition.id} className="workspace-card-container min-h-0 min-w-0">
                    {definition.id === 'insights' ? (
                      <div
                        id="workspace-card-insights"
                        className="workspace-insights-card relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg"
                      >
                        {editing && !mobile && (
                          <div
                            aria-label="Drag LognAI Insights"
                            title="Drag LognAI Insights"
                            className="workspace-drag-handle absolute right-3 top-3 z-20 flex h-6 w-6 items-center justify-center rounded text-muted-foreground"
                          >
                            <GripVertical className="h-4 w-4" aria-hidden="true" />
                          </div>
                        )}
                        <div
                          ref={setInsightsContent}
                          className={`workspace-insights-content min-h-0 flex-1 overflow-auto ${insightsEmpty ? 'workspace-insights-content--empty' : ''}`}
                        >
                          <LatestInsightCard
                            limit={3}
                            onEmptyChange={setInsightsEmpty}
                            onNavigate={(url) => navigate(url)}
                            onViewAll={editing ? undefined : () => navigate('/insights')}
                          />
                        </div>
                      </div>
                    ) : (
                    <WorkspaceCardShell
                      definition={definition}
                      editing={editing && !mobile}
                      hideHeader={definition.id === 'radar'}
                      pinned={draftLayout.pinnedCardIds.includes(definition.id)}
                      position={index}
                      total={orderedDefinitions.length}
                      onMove={moveCard}
                      onTogglePin={(id) => {
                        setDraftLayout(toggleWorkspaceCardPin(draftLayout, id));
                        setSaveError(null);
                      }}
                      onRemove={removeCard}
                    >
                      {renderCardBody(definition.id)}
                    </WorkspaceCardShell>
                    )}
                    {editing && !mobile && <>
                      {(() => {
                        const item = draftLayout.cardLayouts[activeBreakpoint].find((item) => item.i === definition.id);
                        if (!item || item.x + item.w >= WORKSPACE_COLUMNS[activeBreakpoint]) return null;
                        return <WorkspaceInsertSlot label={`Insert beside ${definition.title}`} className="workspace-insert-card--side" canAddCharts={canAddCharts} onChoose={(kind) => {
                          setInsertionIndex(index + 1);
                          if (kind === 'card') openCardPicker(); else if (kind === 'chart') setChartPickerOpen(true); else setKpiPickerOpen(true);
                        }} />;
                      })()}
                      <WorkspaceInsertSlot label={`Insert after ${definition.title}`} className="workspace-insert-card--below" canAddCharts={canAddCharts} onChoose={(kind) => {
                        setInsertionIndex(index + 1);
                        if (kind === 'card') openCardPicker(); else if (kind === 'chart') setChartPickerOpen(true); else setKpiPickerOpen(true);
                      }} />
                    </>}
                  </div>
                ))}
              </Responsive>
            )}
          </section>
          </>
        )}
        {editing && (
          <div
            role="region"
            aria-label="Workspace customization"
            className="sticky bottom-4 z-30 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/25 bg-surface bg-gradient-to-r from-primary/20 via-primary/10 to-primary/5 px-4 py-3 shadow-lg"
          >
            <div>
              <p className="text-sm font-medium text-foreground">Customizing your Workspace</p>
              <p className="text-xs text-muted-foreground">
                Drag to arrange, resize any card, or click the accent strips to add cards. Changes are personal to this company.
              </p>
              {optimizationNote && <p role="status" className="mt-1 max-w-xl text-xs text-primary">{optimizationNote}</p>}
              {optimizationError && <p role="alert" className="mt-1 max-w-xl text-xs text-destructive-foreground">{optimizationError}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {unsaved && (
                <Badge tone="warning" variant="subtle" size="sm">
                  Unsaved changes
                </Badge>
              )}
              <WorkspaceAiButton compact label="Optimize layout" busy={optimizing} disabled={draftLayout.orderedCardIds.length === 0} onClick={() => void optimizeLayout()} />
              <WorkspaceAiButton compact onClick={() => { setInsertionIndex(undefined); openCardPicker(true); }} />
              <Button
                variant="primary"
                size="sm"
                onClick={() => { setInsertionIndex(undefined); openCardPicker(); }}
                disabled={visibleDefinitions.length === 0}
              >
                <Plus className="h-4 w-4" />
                Add card
              </Button>
              <Button variant="primary" size="sm" onClick={() => { setInsertionIndex(undefined); setKpiPickerOpen(true); }} disabled={availableKpiDefinitions.length === 0}>
                <Plus className="h-4 w-4" /> Add KPI
              </Button>
              {canAddCharts && (
                <Button variant="primary" size="sm" onClick={() => { setInsertionIndex(undefined); setChartPickerOpen(true); }}>
                  <Plus className="h-4 w-4" />
                  Add chart
                </Button>
              )}
            </div>
          </div>
        )}
      </PageLayout>

      <ConfirmDialog
        open={pendingHref !== null}
        onOpenChange={(open) => { if (!open) setPendingHref(null); }}
        title="Leave Workspace without saving?"
        description="Your layout changes have not been saved. Leaving now discards them."
        confirmButtonText="Leave without saving"
        cancelButtonText="Stay here"
        onConfirm={() => {
          const href = pendingHref;
          setPendingHref(null);
          if (!href) return;
          if (/^[a-z]+:/i.test(href)) window.location.assign(href);
          else navigate(href);
        }}
      />

      <ConfirmDialog
        open={confirmingReset}
        onOpenChange={setConfirmingReset}
        title="Reset Workspace to the default layout?"
        description="This restores the recommended cards and KPIs for your role, and clears your custom arrangement. The charts you added are kept, and repacked at their default size."
        confirmButtonText="Reset layout"
        onConfirm={resetDraft}
      />
    </>
  );
}
